import React, { useState, useEffect } from 'react';
import { Sparkles, Heart, Flower2, Leaf, Check, Plus, ArrowLeft, ArrowRight, ShieldCheck } from 'lucide-react';
import { BundleItem } from '../data/services';
import { getBundlesList, subscribeToCatalogUpdates, syncBundlesFromRemote } from '../services/catalogService';

interface BundlesSectionProps {
  selectedServices: string[];
  onToggleService: (id: string, name: string, price: number) => void;
  selectedPackageId?: string | null;
  onSelectPackage?: (packageId: string) => void;
  onBackToBundles?: () => void;
}

export const BundlesSection: React.FC<BundlesSectionProps> = ({
  selectedServices,
  onToggleService,
  selectedPackageId = null,
  onSelectPackage,
  onBackToBundles
}) => {
  const [bundlesList, setBundlesList] = useState(getBundlesList);

  useEffect(() => {
    syncBundlesFromRemote().catch(() => {});
    const unsubscribe = subscribeToCatalogUpdates(() => {
      setBundlesList(getBundlesList());
    });
    return unsubscribe;
  }, []);

  const getIcon = (type: BundleItem['iconType']) => {
    switch (type) {
      case 'flower': return <Flower2 size={22} />;
      case 'sparkle': return <Sparkles size={22} />;
      case 'leaf': return <Leaf size={22} />;
      case 'heart': return <Heart size={22} />;
      default: return <Sparkles size={22} />;
    }
  };

  const selectedBundle = selectedPackageId
    ? bundlesList.find((b) => b.id === selectedPackageId)
    : null;

  return (
    <section id="bundles" className="bundles-section">
      <div className="section-container">
        {/* Section Header */}
        {!selectedBundle && (
          <div className="section-title-wrap">
            <span className="section-eyebrow">Curated Packages</span>
            <h2 className="section-main-heading">Blishka Bundles</h2>
            <span className="section-script-subtitle">More care. More value. ♡</span>
          </div>
        )}

        {/* ── 1. SINGLE PACKAGE DETAIL VIEW (When package is expanded) ── */}
        {selectedBundle ? (
          <div className="bundle-detail-layout" style={{ maxWidth: '680px', margin: '0 auto' }}>
            <button
              type="button"
              className="cat-back-btn"
              onClick={onBackToBundles}
              style={{ marginBottom: '1.25rem' }}
            >
              <ArrowLeft size={15} />
              <span>All Bundles &amp; Packages</span>
            </button>

            <div className="bundle-card bundle-detail-card" style={{ padding: '2rem 1.75rem', textAlign: 'center' }}>
              <div className="bundle-icon-wrap" style={{ margin: '0 auto 1rem auto', width: '56px', height: '56px' }}>
                {getIcon(selectedBundle.iconType)}
              </div>

              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'rgba(125,26,56,0.1)', color: 'var(--blishka-berry)', padding: '0.3rem 0.8rem', borderRadius: '999px', fontSize: '0.78rem', fontWeight: 700, marginBottom: '0.6rem' }}>
                <Sparkles size={12} />
                <span>Special Curated Combo</span>
              </div>

              <h2 className="bundle-name" style={{ fontSize: '1.8rem', marginBottom: '0.5rem' }}>
                {selectedBundle.name}
              </h2>

              <p style={{ fontSize: '0.92rem', color: 'var(--color-text-body)', maxWidth: '480px', margin: '0 auto 1.5rem auto', lineHeight: 1.5 }}>
                {`All-inclusive home parlour package featuring ${selectedBundle.servicesIncluded.join(', ')} with complete doorstep salon hygiene.`}
              </p>

              {/* Price & Savings Spotlight */}
              <div style={{ background: 'linear-gradient(135deg, #fff7f8 0%, #feecf0 100%)', borderRadius: '16px', padding: '1.25rem', border: '1px solid rgba(125,26,56,0.12)', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginBottom: '0.4rem' }}>
                  <span style={{ fontSize: '2.2rem', fontWeight: 800, color: 'var(--blishka-berry)' }}>
                    ₹{selectedBundle.price}
                  </span>
                  <span style={{ fontSize: '1.1rem', textDecoration: 'line-through', color: 'var(--color-text-muted)' }}>
                    Value ₹{selectedBundle.originalValue}
                  </span>
                </div>
                <div style={{ display: 'inline-block', background: 'var(--blishka-berry)', color: '#ffffff', padding: '0.35rem 0.9rem', borderRadius: '999px', fontSize: '0.82rem', fontWeight: 700 }}>
                  You Save ₹{selectedBundle.saveAmount} ({Math.round((selectedBundle.saveAmount / selectedBundle.originalValue) * 100)}% OFF)
                </div>
              </div>

              {/* Included Services Checklist */}
              <div style={{ textAlign: 'left', marginBottom: '1.75rem' }}>
                <h4 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--blishka-berry)', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Check size={16} />
                  <span>Treatments Included in this Package ({selectedBundle.servicesIncluded.length}):</span>
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.6rem' }}>
                  {selectedBundle.servicesIncluded.map((srv, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        background: '#ffffff',
                        border: '1px solid rgba(125,26,56,0.1)',
                        padding: '0.6rem 0.85rem',
                        borderRadius: '10px',
                        fontSize: '0.88rem',
                        fontWeight: 600,
                        color: 'var(--color-text-main)'
                      }}
                    >
                      <Check size={14} color="var(--blishka-berry)" style={{ flexShrink: 0 }} />
                      <span>{srv}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Hygiene Guarantee */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap', padding: '0.85rem', background: '#fafafa', borderRadius: '12px', marginBottom: '1.5rem', fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <ShieldCheck size={15} color="var(--blishka-berry)" />
                  <span>100% Single-Use Disposables</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <Heart size={15} color="var(--blishka-berry)" />
                  <span>Verified Female Specialists</span>
                </div>
              </div>

              {/* Add Bundle Button */}
              {(() => {
                const isSelected = selectedServices.includes(selectedBundle.id);
                return (
                  <button
                    type="button"
                    onClick={() => onToggleService(selectedBundle.id, selectedBundle.name, selectedBundle.price)}
                    className={`btn ${isSelected ? 'btn-berry' : 'btn-outline'}`}
                    style={{ width: '100%', padding: '0.9rem', fontSize: '1rem', fontWeight: 700 }}
                  >
                    {isSelected ? (
                      <>
                        <Check size={18} />
                        <span>Selected in Cart</span>
                      </>
                    ) : (
                      <>
                        <Plus size={18} />
                        <span>Add Package to Cart (₹{selectedBundle.price})</span>
                      </>
                    )}
                  </button>
                );
              })()}
            </div>
          </div>
        ) : (
          /* ── 2. ALL BUNDLES GRID VIEW ── */
          <div className="bundles-grid">
            {bundlesList.map((bundle) => {
              const isSelected = selectedServices.includes(bundle.id);
              return (
                <div key={bundle.id} className="bundle-card">
                  <div className="bundle-icon-wrap">
                    {getIcon(bundle.iconType)}
                  </div>

                  <h3
                    className="bundle-name"
                    style={{ cursor: onSelectPackage ? 'pointer' : 'default' }}
                    onClick={() => onSelectPackage && onSelectPackage(bundle.id)}
                  >
                    {bundle.name}
                  </h3>

                  <ul className="bundle-services-list">
                    {bundle.servicesIncluded.map((srv, idx) => (
                      <li key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem' }}>
                        <Check size={12} color="var(--blishka-berry)" />
                        <span>{srv}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="bundle-price-box">
                    <div className="bundle-price">₹{bundle.price}</div>
                    <div className="bundle-original-val">(Value ₹{bundle.originalValue})</div>
                  </div>

                  <div>
                    <span className="bundle-save-tag">
                      Save ₹{bundle.saveAmount}
                    </span>
                  </div>

                  {onSelectPackage && (
                    <button
                      type="button"
                      onClick={() => onSelectPackage(bundle.id)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--blishka-berry)',
                        fontSize: '0.82rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        padding: '0.3rem 0',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.25rem',
                        marginBottom: '0.5rem'
                      }}
                    >
                      <span>View Package Details</span>
                      <ArrowRight size={13} />
                    </button>
                  )}

                  <button
                    onClick={() => onToggleService(bundle.id, bundle.name, bundle.price)}
                    className={`btn ${isSelected ? 'btn-berry' : 'btn-outline'} btn-sm`}
                    style={{ width: '100%', marginTop: 'auto' }}
                  >
                    {isSelected ? (
                      <>
                        <Check size={14} />
                        <span>Selected</span>
                      </>
                    ) : (
                      <>
                        <Plus size={14} />
                        <span>Add Bundle</span>
                      </>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
